import { url, tokenRef } from "api";

// ==================== HELPERS ====================
const getHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  const userToken = localStorage.getItem(tokenRef);
  if (userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.data = data;
    if (data && data.index !== undefined) error.index = data.index;
    if (data && data.openIssues !== undefined) error.openIssues = data.openIssues;
    throw error;
  }
  return data;
};

// ==================== GANTT ====================

/**
 * Get the full gantt feed:
 * {items:[{...item, issueCounts, healthStatus}], issues, health}
 */
// THE SCHEDULE AS A FILE (owner, 2026-10-20): every activity the caller may
// see - a partner's portion for a partner - and `company` narrows an owner's
// to one contractor. ext: "pdf" | "xlsx" | "csv".
export const exportSchedule = async (projectId, ext, { company } = {}) => {
  const res = await fetch(`${url}/projects/${projectId}/gantt/export.${ext}${company ? `?company=${encodeURIComponent(company)}` : ""}`, { headers: getHeaders() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || `schedule.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
  return { count: Number(res.headers.get("X-Export-Count") || 0) };
};

export const getGanttFeed = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * List plan items only
 */
export const listGanttItems = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Create a single plan item
 * body: {type, name, startDate, endDate, discipline?, areaKey?, level?,
 *        grid?, responsibleCompanyId?, recurrence?, sortIndex?}
 */
export const createGanttItem = async (projectId, body) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Bulk-create plan items → {created, items}.
 * On a bad row the thrown error carries .index (failing row number).
 */
export const bulkCreateGanttItems = async (projectId, items) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/bulk`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ items }),
  });
  return handleResponse(response);
};

/**
 * Partial update of a plan item. {status:"done"} may throw a 409 error
 * carrying .openIssues unless body has force:true.
 */
export const updateGanttItem = async (projectId, gid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}`, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Set a LEAF activity's progress (0-100). Parents are refused (their % is
 * the average of their tasks). Returns { item, ancestors: [{_id, progress}] }
 * so the chart repaints the parent chain without reloading.
 */
export const setGanttItemProgress = async (projectId, gid, progress) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/progress`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ progress }),
  });
  return handleResponse(response);
};

/**
 * Delete a plan item
 */
export const deleteGanttItem = async (projectId, gid) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Sign off a plan item. Throws a 409 error carrying .openIssues and
 * .data.unsignedChildren while the row still has open issues or dated
 * child activities not yet signed off; body {force:true} records it anyway.
 * body.signOffDate sets the date in the same call (an unsaved editor row).
 */
export const signOffGanttItem = async (projectId, gid, body = {}) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/sign-off`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/** Withdraw a sign-off (the date stays; the activity still needs one). */
export const withdrawSignOffGanttItem = async (projectId, gid) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/sign-off`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * MOVE AN ACTIVITY AND EVERYTHING UNDER IT BY N WORKING DAYS.
 *
 * PREVIEWS BY DEFAULT: without `apply` the server computes and returns what
 * it WOULD do - the rows that move with their before and after dates, the
 * rows it is leaving and why, the links that cross the edge of the move -
 * and writes nothing. The same call with `apply: true` performs it, from the
 * same computation, so the confirmation and the write cannot disagree.
 *
 * A 409 means the branch spans more than one calendar: N working days is a
 * different number of calendar days on each, so the branch does not move as
 * one piece. The error body carries the whole preview, and
 * `allowMixedCalendars: true` goes ahead anyway.
 */
export const offsetGanttSubtree = async (projectId, gid, { days, apply = false, allowMixedCalendars = false } = {}) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/offset`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ days, apply, allowMixedCalendars }),
  });
  return handleResponse(response);
};

// ─── Claims ────────────────────────────────────────────────────────────
//
// A partner volunteering for work nobody has been assigned. The claim
// carries the AREA, because claiming is how an activity gets filed into
// the area breakdown. Nothing changes on the schedule until the project
// owner approves it in the Review Center.

/** Partner side: ask for an unassigned activity. */
export const claimGanttItem = async (projectId, gid, { areaId, note } = {}) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/claim`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ areaId, note }),
  });
  return handleResponse(response);
};

/** Partner side: take back a claim that has not been reviewed yet. */
export const withdrawGanttClaim = async (projectId, gid) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/claim`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/** Owner side: claims waiting for review on this project. */
export const listPendingClaims = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/pending-claims`, {
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/** Owner side: approve hands the work over and files it into the area. */
export const reviewGanttClaim = async (projectId, gid, action, note = "") => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/${gid}/claim/${action}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ note }),
  });
  return handleResponse(response);
};

// ==================== BUILD FROM LEVELS, AREAS & SYSTEMS ====================
// back/routes/systemTasks.js (owner, 2026-10-15). preview writes nothing;
// apply recomputes the previewed plan and refuses when it changed; undo puts
// back what one build changed.
const systemTasksPost = async (projectId, path, body) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/system-tasks/${path}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body || {}),
  });
  return handleResponse(response);
};
export const previewSystemTasks = (projectId, body) => systemTasksPost(projectId, "preview", body);
export const applySystemTasks = (projectId, body) => systemTasksPost(projectId, "apply", body);
export const undoSystemTasks = (projectId, runId, force = false) => systemTasksPost(projectId, "undo", { runId, force });

// ============ ONE LEVEL PAGE'S AREAS AND SYSTEMS ONTO THE SCHEDULE ============
// back/routes/levelScheduleGen.js (owner, 2026-10-26). The dropdown on
// Levels, Areas & Systems reads the levels; preview writes nothing and
// carries what each activity looks like now AND would look like; apply
// recomputes it and refuses a plan the person was not looking at.
const levelAreasPost = async (projectId, path, body) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/level-areas/${path}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body || {}),
  });
  return handleResponse(response);
};
export const getScheduleLevels = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/gantt-items/level-areas/levels`, { headers: getHeaders() });
  return handleResponse(response);
};
export const previewLevelAreas = (projectId, body) => levelAreasPost(projectId, "preview", body);
export const applyLevelAreas = (projectId, body) => levelAreasPost(projectId, "apply", body);
export const undoLevelAreas = (projectId, runId, force = false) => levelAreasPost(projectId, "undo", { runId, force });
